/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useRef } from "react";

const DEFAULT_EVENTS = ["mousedown", "touchstart"];

const isInsideModal = (node) =>
  !!node && typeof node.closest === "function" && node.closest("dialog[open]") !== null;

const listener = (handler, nodes, ref) => (event) => {
  const { target } = event ?? {};
  const watchedNodes = Array.isArray(nodes) ? nodes : [ref.current];
  const watchesInsideModal = watchedNodes.some(isInsideModal);

  if (isInsideModal(target) && !watchesInsideModal) {
    return;
  }

  if (Array.isArray(nodes)) {
    const shouldIgnore = !document.body.contains(target) && target.tagName !== "HTML";
    const shouldTrigger = nodes.every((node) => !!node && !event.composedPath().includes(node));

    shouldTrigger && !shouldIgnore && handler();
  } else if (ref.current && !ref.current.contains(target)) {
    handler();
  }
};

export function useClickOutside(handler, nodes) {
  const ref = useRef();

  useEffect(() => {
    const boundListener = listener(handler, nodes, ref);

    DEFAULT_EVENTS.forEach((fn) => document.addEventListener(fn, boundListener));

    return () => {
      DEFAULT_EVENTS.forEach((fn) => document.removeEventListener(fn, boundListener));
    };
  }, [handler, nodes]);

  return ref;
}
